import { letterSpacing } from '@/constants/tokens';
import type { ReactNode } from 'react';
import { Button, getTokenValue, Spinner, Text } from 'tamagui';

/**
 * PSButton — the rectangular primary button (atom).
 *
 * Filled purple surface, white semibold label, `$base` rounded corners — the
 * "Book Now" / "Login & get Access Pass" button from the designs, expressed
 * entirely in `constants/tokens.ts` tokens.
 *
 * Kept deliberately small (CLAUDE.md "Component Complexity"): one filled look,
 * an optional trailing icon (the callout CTA's arrow), a disabled state, and a
 * loading state. `variant` grows one value per real call site — an
 * outline style, `destructive` (the `$error` fill behind Settings'
 * delete-account confirm), and `toolbar` (for buttons overlaid on
 * `darkToolbar`) — no speculative variants. Each variant's look lives as one
 * entry in `VARIANT_STYLES` below rather than a ternary per style prop, so
 * adding one is a single object, not an edit-every-ternary hunt.
 *
 * ── Loading is NOT disabled ───────────────────────────────────────────
 * An in-flight button is working, not unavailable, so it keeps FULL opacity and
 * swaps its label for the caller's progressive verb ("Book Now" → "Booking")
 * plus a spinner. Only the dim is withheld: `loading` still folds into Tamagui's
 * `disabled` prop, because that's the mechanism that detaches the press handlers
 * (a second tap must not fire the request again). Hence `disabled` drives the
 * opacity while `disabled || loading` drives pressability — they're deliberately
 * different expressions, not a typo.
 *
 * The verb is the CALLER's, not derived here: only the screen knows that "Book
 * Now" becomes "Booking" and "Confirm" becomes "Confirming", and no string
 * transform gets that right. The prop pair is union-typed so `loading` can't be
 * passed without it — a spinner next to the idle label is the bug that type
 * catches (see the guide's "constrain props at the type level").
 */
type PSButtonVariant =
  | 'primary'
  | 'outline'
  | 'outlineBrand'
  | 'destructive'
  | 'toolbar';

type PSButtonProps = {
  title: string;
  onPress?: () => void;
  disabled?: boolean;
  /** Optional trailing element, e.g. an arrow icon on a CTA. Hidden while loading. */
  icon?: ReactNode;
  fullWidth?: boolean;
  textColor?: '$color.textDark' | '$color.textWhite';
  variant?: PSButtonVariant;
} & (
  | { loading?: false; loadingTitle?: never }
  /** `loading` and `loadingTitle` travel together — one without the other won't compile. */
  | { loading: boolean; loadingTitle: string }
);

const VARIANT_STYLES = {
  primary: {
    backgroundColor: '$brand',
    // eslint-disable-next-line local-rules/no-hardcoded-tokens -- 0 is "no border", not a token value.
    borderWidth: 0,
    borderColor: undefined,
    px: '$xl',
    fontSize: '$base',
    fontWeight: '$semibold',
    textColor: '$color.textWhite',
    minHeight: 40,
  },
  outline: {
    backgroundColor: '$transparent',
    borderWidth: '$borderWidth.hairline',
    borderColor: '$color.brandDark',
    px: '$xl',
    fontSize: '$base',
    fontWeight: '$medium',
    textColor: '$color.textDark',
    minHeight: 40,
  },
  outlineBrand: {
    backgroundColor: '$transparent',
    borderWidth: '$borderWidth.hairline',
    borderColor: '$brand',
    px: '$xl',
    fontSize: '$base',
    fontWeight: '$medium',
    textColor: '$color.brand',
    minHeight: 40,
  },
  destructive: {
    backgroundColor: '$error',
    // eslint-disable-next-line local-rules/no-hardcoded-tokens -- 0 is "no border", not a token value.
    borderWidth: 0,
    borderColor: undefined,
    px: '$xl',
    fontSize: '$base',
    fontWeight: '$semibold',
    textColor: '$color.textWhite',
    minHeight: 40,
  },
  toolbar: {
    backgroundColor: '$transparent',
    borderWidth: '$borderWidth.hairline',
    borderColor: '$color.brandSecondary',
    px: '$md',
    fontSize: '$small',
    fontWeight: '$semibold',
    textColor: '$color.brandSecondary',
    minHeight: 32,
  },
} as const satisfies Record<PSButtonVariant, unknown>;

export default function PSButton({
  title,
  onPress,
  disabled = false,
  loading = false,
  loadingTitle,
  icon,
  fullWidth = false,
  textColor,
  variant = 'primary',
}: PSButtonProps) {
  const styles = VARIANT_STYLES[variant];
  const blocked = disabled || loading;

  return (
    <Button
      // Not Tamagui's `disabled` variant: on native it flips the Frame's
      // `pointerEvents` between "none"/"auto", and on Android that flip
      // (exactly what happens the instant a form goes from invalid to
      // valid) can leave a stale, undersized touch region — tappable only
      // over the label — until a later layout pass. No-op the press instead
      // so `pointerEvents` never changes.
      onPress={blocked ? undefined : onPress}
      aria-disabled={blocked}
      opacity={disabled ? 0.4 : 1}
      pressStyle={blocked ? undefined : { opacity: 0.66 }}
      backgroundColor={styles.backgroundColor}
      rounded="$base"
      // Tamagui's Button defaults to a fixed `height` (44, from the `$true`
      // size token) that doesn't grow for wrapped multi-line labels — on web
      // that's a hard CSS height, so a 2-line label gets clipped against it
      // instead of the box growing. `auto` lets it size to content again;
      // `minHeight` keeps the touch target floor for short labels.
      // @ts-ignore
      height="auto"
      minHeight={styles.minHeight}
      borderWidth={styles.borderWidth}
      borderColor={styles.borderColor}
      px={styles.px}
      py="$xs"
      gap="$sm"
      // @ts-ignore
      width={fullWidth ? '100%' : undefined}
    >
      <Text
        fontFamily="$body"
        fontSize={styles.fontSize}
        fontWeight={styles.fontWeight}
        color={textColor ?? styles.textColor}
        textAlign="center"
        letterSpacing={
          variant === 'toolbar'
            ? letterSpacing.psButtonToolbar
            : letterSpacing.normal
        }
        // letter-spacing adds trailing tracking after the LAST character too,
        // so it pads the right side more than the left inside px="$md". Pull
        // that trailing space back so the label sits centered again.
        marginRight={
          variant === 'toolbar' ? -letterSpacing.psButtonToolbar : undefined
        }
      >
        {loading ? loadingTitle : title}
      </Text>
      {/* The spinner takes the trailing slot, so a CTA's arrow steps aside while
          the request is in flight rather than sitting beside a spinner. */}
      {loading ? (
        // Tamagui's Spinner DOES resolve a `$token` color, but through
        // `useTheme()` — and this app's single theme only defines
        // background/borderColor/color, so `$textWhite` (a plain color TOKEN)
        // would miss and fall back to the platform gray. Bridge it with
        // getTokenValue like PSIcon does; a raw value skips Spinner's `$` branch.
        <Spinner size="small" color={getTokenValue(styles.textColor)} />
      ) : (
        icon
      )}
    </Button>
  );
}
